import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import MobileStepper, { MobileStep } from "./MobileStepper";

export interface MobileHeaderMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface MobileFlowHeaderProps {
  title: string;
  steps: MobileStep[];
  onBack: () => void;
  onStepClick?: (index: number) => void;
  menuItems?: MobileHeaderMenuItem[];
}

const MobileFlowHeader: React.FC<MobileFlowHeaderProps> = ({
  title,
  steps,
  onBack,
  onStepClick,
  menuItems = [],
}) => {
  const theme = useTheme();
  const classes = MobileSignRequestStyles(theme);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const menuOpen = Boolean(menuAnchor);

  return (
    <Box component="header" sx={classes.header}>
      <Box sx={classes.titleRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            sx={classes.headerIcon}
            onClick={onBack}
            size="small"
            aria-label="Go back"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography component="h1" sx={classes.titleText}>
            {title}
          </Typography>
        </Box>

        {menuItems.length > 0 && (
          <>
            <IconButton
              sx={classes.headerIcon}
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? "mobile-flow-header-menu" : undefined}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              id="mobile-flow-header-menu"
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              MenuListProps={{ "aria-label": "More actions" }}
            >
              {menuItems.map((item, idx) => (
                <MenuItem
                  key={item.label + idx}
                  disabled={item.disabled}
                  onClick={() => {
                    item.onClick();
                    setMenuAnchor(null);
                  }}
                  sx={{ minHeight: 44, gap: 1.5 }}
                >
                  {item.icon}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>

      <MobileStepper steps={steps} onStepClick={onStepClick} />
    </Box>
  );
};

export default MobileFlowHeader;
